/**
 * Formatador rigoroso de tempos de natação
 * Padrão: M:SS.cc (acima de 1 minuto) ou SS.cc (abaixo de 1 minuto)
 * Sempre com 2 casas decimais para detectar erros de input manual
 */

/**
 * Formata tempo de natação a partir de milissegundos
 * @param {number} ms - Tempo em milissegundos
 * @returns {string} Tempo formatado (ex: "1:02.34" ou "52.34")
 */
export const formatSwimmingTime = (ms) => {
  if (!ms || ms === 0) return "-";
  
  const totalSeconds = ms / 1000;
  const min = Math.floor(totalSeconds / 60);
  const sec = (totalSeconds % 60).toFixed(2);
  
  // Se > 1 minuto: M:SS.cc
  // Se < 1 minuto: SS.cc
  return min > 0 ? `${min}:${sec.padStart(5, '0')}` : sec;
};

/**
 * Formata tempo a partir de segundos (com decimais)
 * @param {number} seconds - Tempo em segundos (ex: 62.34)
 * @returns {string} Tempo formatado
 */
export const formatSwimmingTimeFromSeconds = (seconds) => {
  if (!seconds || seconds === 0) return "-";
  return formatSwimmingTime(seconds * 1000);
};

/**
 * Formata tempo a partir de centésimos (usado em alguns backends legados)
 * @param {number} centis - Tempo em centésimos de segundo
 * @returns {string} Tempo formatado
 */
export const formatSwimmingTimeFromCentis = (centis) => {
  if (!centis || centis === 0) return "-";
  return formatSwimmingTime(centis * 10); // 1 centésimo = 10ms
};

/**
 * Detecta e formata tempo de qualquer formato do backend
 * Fallback robusto com conversão automática
 * @param {Object} result - Objeto de resultado do backend
 * @returns {string} Tempo formatado ou "-"
 */
export const formatSwimmingTimeRobust = (result) => {
  if (!result) return "-";
  
  // 1. Se já vem formatado do backend, usar (confiar no servidor)
  if (result.time_formatted) return result.time_formatted;
  
  // 2. Se vem em milissegundos (padrão recomendado)
  if (result.time_ms) return formatSwimmingTime(result.time_ms);
  
  // 3. Se vem em centésimos (legado)
  if (result.time_centis) return formatSwimmingTimeFromCentis(result.time_centis);
  
  // 4. Se vem em segundos (decimal)
  if (result.time_seconds) return formatSwimmingTimeFromSeconds(result.time_seconds);
  
  // 5. Campo genérico 'time' (tentar detectar tipo)
  if (result.time) {
    // Se for string, assumir que já está formatado
    if (typeof result.time === 'string') return result.time;
    
    // Se for número, assumir segundos (padrão mais comum)
    if (typeof result.time === 'number') {
      return formatSwimmingTimeFromSeconds(result.time);
    }
  }
  
  // 6. Não conseguiu formatar
  return "-";
};

/**
 * Calcula diferença de tempo em segundos
 * @param {number} time1_ms - Tempo 1 em ms
 * @param {number} time2_ms - Tempo 2 em ms
 * @returns {string} Diferença formatada com sinal (ex: "+0.45")
 */
export const formatTimeDifference = (time1_ms, time2_ms) => {
  if (!time1_ms || !time2_ms) return "-";
  
  const diff_seconds = Math.abs((time1_ms - time2_ms) / 1000);
  const sign = time1_ms > time2_ms ? "+" : "-";
  
  return `${sign}${diff_seconds.toFixed(2)}`;
};

/**
 * Calcula médias de tempo (para top4, top8, etc)
 * @param {Array<number>} times_ms - Array de tempos em ms
 * @returns {string} Média formatada
 */
export const formatAverageTime = (times_ms) => {
  if (!times_ms || times_ms.length === 0) return "-";
  
  const validTimes = times_ms.filter(t => t && t > 0);
  if (validTimes.length === 0) return "-";
  
  const avg_ms = validTimes.reduce((sum, t) => sum + t, 0) / validTimes.length;
  return formatSwimmingTime(avg_ms);
};

/**
 * Calcula desvio padrão de tempos (indicador de competitividade)
 * @param {Array<number>} times_ms - Array de tempos em ms
 * @returns {number} Desvio padrão em segundos
 */
export const calculateStandardDeviation = (times_ms) => {
  if (!times_ms || times_ms.length < 2) return 0;
  
  const validTimes = times_ms.filter(t => t && t > 0);
  if (validTimes.length < 2) return 0;
  
  const avg = validTimes.reduce((sum, t) => sum + t, 0) / validTimes.length;
  const variance = validTimes.reduce((sum, t) => sum + Math.pow(t - avg, 2), 0) / validTimes.length;
  
  return Math.sqrt(variance) / 1000; // Retorna em segundos
};

/**
 * Calcula gap de pódio (1º → 3º)
 * @param {Array<Object>} results - Array de resultados ordenados
 * @returns {string} Gap formatado (ex: "0.87s")
 */
export const calculatePodiumGap = (results) => {
  if (!results || results.length < 3) return "-";
  
  const first = results[0]?.time_ms;
  const third = results[2]?.time_ms;
  
  if (!first || !third) return "-";
  
  const gap_seconds = (third - first) / 1000;
  return `${gap_seconds.toFixed(2)}s`;
};
